import { jsx } from 'react/jsx-runtime';
import { PureComponent, createRef } from 'react';

let lazyLoadObserver; // Static style object for the wrapper element

const WRAPPER_STYLES = {
  width: '100%',
  height: '100%'
};

const observerHandler = entries => {
  if (!entries) return;
  entries.forEach(entry => {
    if (lazyLoadObserver && entry.isIntersecting) {
      // If the observed element is intersecting/visible, fire the handler
      // and stop observing
      const el = entry.target;

      if (lazyLoadObserver.handlers.get) {
        const handler = lazyLoadObserver.handlers.get(el);

        if (handler) {
          handler();
        }
      }

      if (lazyLoadObserver.observer.unobserve) {
        lazyLoadObserver.observer.unobserve(entry.target);
      }
    }
  });
};

class LazyLoad extends PureComponent {
  constructor(props) {
    super(props);
    this.wrapperRef = createRef();
    this.state = {
      isVisible: false
    };
    this.handleIntersection = this.handleIntersection.bind(this);
  }
  /** If we're disabled, or IntersectionObservers are not a thing in this environment, return true. */


  componentDidMount() {
    if (this.noGo) return;

    if (!lazyLoadObserver) {
      const {
        threshold
      } = this.props; // If the lazyLoadObserver isn't initialized, set it up

      lazyLoadObserver = {
        handlers: new WeakMap(),
        observer: new IntersectionObserver(observerHandler, {
          threshold,
          // Begin loading images when they're 260px below the viewport
          // This is an a11y requirement.
          rootMargin: '0px 0px 260px 0px'
        })
      };
    } // Attach this instance's observer and handler


    if (!this.wrapperRef.current) return;
    lazyLoadObserver.handlers.set(this.wrapperRef.current, this.handleIntersection);
    lazyLoadObserver.observer.observe(this.wrapperRef.current);
  }

  componentWillUnmount() {
    if (this.noGo) return;

    if (lazyLoadObserver) {
      if (lazyLoadObserver.observer.unobserve) {
        lazyLoadObserver.observer.unobserve(this.wrapperRef.current);
      }

      if (lazyLoadObserver.handlers.delete) {
        lazyLoadObserver.handlers.delete(this.wrapperRef.current);
      }
    }
  }

  get noGo() {
    return !this.props.enabled || typeof IntersectionObserver === 'undefined';
  }

  handleIntersection() {
    this.setState({
      isVisible: true
    });
  }

  render() {
    const {
      children,
      className,
      placeholder,
      enabled
    } = this.props;
    const {
      isVisible
    } = this.state;
    return jsx("div", Object.assign({
      className: className,
      ref: this.wrapperRef,
      style: WRAPPER_STYLES
    }, {
      children: !enabled || isVisible ? children : placeholder
    }), void 0);
  }

}
LazyLoad.displayName = 'LazyLoad';
LazyLoad.defaultProps = {
  threshold: 0
};

export { LazyLoad };
